/**
 * src/admin-ui/src/components/edge-table/ColumnFilterPopover.tsx
 *
 * Excel-like column filter in the any-classifier header style: the whole
 * column header label is the trigger (highlighted while a filter is
 * active), opening a square bordered panel with typed conditions
 * (substring/equality for text, =/>/< for numbers, AND/OR chaining) and
 * a checkbox list over the unique values of the column.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import {
  ColumnFilterState,
  ColumnKind,
  FilterConnective,
  NumberOp,
  TextOp,
  emptyFilter,
  isFilterActive,
} from "../../utils/usageTableFilters";

const TEXT_OP_LABELS: Record<TextOp, string> = {
  contains: "содержит",
  equals: "равно",
};

const NUMBER_OP_LABELS: Record<NumberOp, string> = {
  eq: "=",
  gt: ">",
  lt: "<",
};

/**
 * Full-width header trigger button (reference `headerFilterZone`): fills
 * the header cell, inherits the uppercase header typography and shows the
 * active filter with a primary-color tint.
 */
const HeaderTrigger = styled("button", {
  shouldForwardProp: (prop) => prop !== "numeric" && prop !== "active",
})<{ numeric?: boolean; active?: boolean }>(({ theme, numeric, active }) => ({
  width: "100%",
  minWidth: 0,
  minHeight: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: numeric ? "flex-end" : "flex-start",
  padding: theme.spacing(0.5, 1),
  border: "none",
  borderRadius: 0,
  background: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
  color: active ? theme.palette.primary.main : "inherit",
  font: "inherit",
  letterSpacing: "inherit",
  textTransform: "inherit",
  textAlign: numeric ? "right" : "left",
  cursor: "pointer",
  transition: "background-color 150ms ease",
  "&:hover": {
    background: active
      ? alpha(theme.palette.primary.main, 0.14)
      : theme.palette.action.hover,
  },
}));

const TriggerLabel = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
});

interface ColumnFilterPopoverProps {
  /** Column title shown in the header and used for accessibility labels. */
  label: string;
  kind: ColumnKind;
  /** Right-align the trigger content for numeric columns. */
  numeric?: boolean;
  /** Unique display values of the column (checkbox list). */
  values: string[];
  filter: ColumnFilterState;
  onChange: (next: ColumnFilterState) => void;
}

const ColumnFilterPopover: React.FC<ColumnFilterPopoverProps> = ({
  label,
  kind,
  numeric,
  values,
  filter,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const active = isFilterActive(filter);

  const conditions: { op: string; value: string }[] =
    kind === "number" ? filter.numberConditions : filter.textConditions;

  const setConditions = (next: { op: string; value: string }[]): void => {
    if (kind === "number") {
      onChange({
        ...filter,
        numberConditions: next as ColumnFilterState["numberConditions"],
      });
    } else {
      onChange({
        ...filter,
        textConditions: next as ColumnFilterState["textConditions"],
      });
    }
  };

  const addCondition = (): void => {
    const op = kind === "number" ? "eq" : "contains";
    setConditions([...conditions, { op, value: "" }]);
  };

  const updateCondition = (
    index: number,
    patch: Partial<{ op: string; value: string }>,
  ): void => {
    setConditions(
      conditions.map((condition, i) =>
        i === index ? { ...condition, ...patch } : condition,
      ),
    );
  };

  const removeCondition = (index: number): void => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const toggleValue = (value: string): void => {
    const current = filter.selected === null ? values : filter.selected;
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    onChange({
      ...filter,
      selected: next.length === values.length ? null : next,
    });
  };

  const allSelected = filter.selected === null;
  const opLabels = kind === "number" ? NUMBER_OP_LABELS : TEXT_OP_LABELS;

  return (
    <>
      <HeaderTrigger
        type="button"
        numeric={numeric}
        active={active}
        aria-label={`Фильтр: ${label}`}
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <TriggerLabel>{label}</TriggerLabel>
      </HeaderTrigger>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 280,
              border: 1,
              borderColor: "divider",
              borderRadius: 0,
              boxShadow:
                "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
            },
          },
        }}
      >
        <Box sx={{ p: 1, width: 300, display: "grid", gap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {label}
          </Typography>

          {conditions.map((condition, index) => (
            <Box
              key={index}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <Select
                size="small"
                value={condition.op}
                sx={{ minWidth: kind === "number" ? 64 : 130 }}
                onChange={(event) =>
                  updateCondition(index, { op: event.target.value })
                }
              >
                {Object.entries(opLabels).map(([op, opLabel]) => (
                  <MenuItem key={op} value={op}>
                    {opLabel}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                fullWidth
                value={condition.value}
                placeholder={kind === "number" ? "Число" : "Текст"}
                inputProps={
                  kind === "number" ? { inputMode: "decimal" } : undefined
                }
                onChange={(event) =>
                  updateCondition(index, { value: event.target.value })
                }
              />
              <IconButton
                size="small"
                aria-label="Убрать условие"
                onClick={() => removeCondition(index)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Button size="small" onClick={addCondition}>
              + Условие
            </Button>
            {conditions.length > 1 && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={filter.connective}
                onChange={(_, value: FilterConnective | null) => {
                  if (value) onChange({ ...filter, connective: value });
                }}
                aria-label="Объединение условий"
              >
                <ToggleButton value="and">И</ToggleButton>
                <ToggleButton value="or">ИЛИ</ToggleButton>
              </ToggleButtonGroup>
            )}
            {active && (
              <Button
                size="small"
                sx={{ marginLeft: "auto" }}
                onClick={() => onChange(emptyFilter())}
              >
                Сбросить
              </Button>
            )}
          </Box>

          <Divider />

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={
                    !allSelected && (filter.selected?.length ?? 0) > 0
                  }
                  onChange={() =>
                    onChange({ ...filter, selected: allSelected ? [] : null })
                  }
                />
              }
              label="Выбрать всё"
            />
            <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
              {values.map((value) => (
                <FormControlLabel
                  key={value}
                  sx={{ display: "flex" }}
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected || filter.selected!.includes(value)}
                      onChange={() => toggleValue(value)}
                    />
                  }
                  label={
                    <Typography variant="body2" noWrap>
                      {value}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ColumnFilterPopover;
